/* eslint-disable import/no-extraneous-dependencies */
import { InputWithLabel } from '@adotel/components'
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation
} from '@apollo/client'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import {
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Slide,
  Typography
} from '@material-ui/core'
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined'
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined'
import EditIcon from '@material-ui/icons/Edit'
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  PRICE_CLASSES_GENERATE_MUTATION,
  PRICE_CLASSES_MODIFY_MUTATION
} from '../../graphql/priceClass'
import { persianRegex } from '../../helpers/regex'
import { getCurrenciesAvailable } from '../../helpers/userData'
import { getDirection, getTranslate, lang } from '../../localization'
import { Currency } from '../../types/currency'
import {
  PriceActionForGenerate,
  PriceActionWithEditMode,
  PriceClass,
  TypeName
} from '../../types/priceClass'
import { Multilingual } from '../../types/roomtype'
import AddPriceActionForm from './AddPriceActionForm'
import PriceActionRow from './PriceActionRow'
import { netOutputCurrency, recalcPriceActionErrors } from './priceActionChain'
import useStyles from './styles/GeneratePriceClassModal.styles'

interface GeneratePriceClassModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      priceClasses: PriceClass[]
    }>
  >
  data?: PriceClass
}

const persianTranslation: Multilingual = {
  label: 'Iran, Islamic Republic of',
  nativeLabel: 'فارسی',
  value: ''
}

const normalizeNameTranslatedList = (
  translatedList?: Multilingual[],
  fallbackFA = ''
): Multilingual[] => {
  const filteredList = (translatedList || []).filter(Boolean)
  const hasPersian = filteredList.some(
    (item) =>
      item.label === persianTranslation.label ||
      item.nativeLabel === persianTranslation.nativeLabel
  )

  if (hasPersian) {
    return filteredList
  }

  return [
    {
      ...persianTranslation,
      value: fallbackFA
    },
    ...filteredList
  ]
}

const getPersianTranslationValue = (translatedList: Multilingual[]) =>
  translatedList.find(
    (item) =>
      item.label === persianTranslation.label ||
      item.nativeLabel === persianTranslation.nativeLabel
  )?.value || ''

const GeneratePriceClassModal: React.FC<GeneratePriceClassModalProps> = ({
  isOpen,
  onClose,
  refetch,
  data
}) => {
  const classes = useStyles()

  function generateRandomString(length: number): string {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length)
      result += characters.charAt(randomIndex)
    }

    return result
  }

  const [priceClassName, setPriceClassName] = useState<TypeName>(
    data?.id
      ? {
          EN: data.name,
          FA: getPersianTranslationValue(
            normalizeNameTranslatedList(data.nameTranslatedList)
          )
        }
      : {
          EN: '',
          FA: ''
        }
  )
  const [priceClassNameTranslatedList, setPriceClassNameTranslatedList] =
    useState<Multilingual[]>(
      data?.id
        ? normalizeNameTranslatedList(
            data.nameTranslatedList,
            getPersianTranslationValue(data.nameTranslatedList)
          )
        : [persianTranslation]
    )
  const [currencies, setCurrencies] = useState<{
    input: Currency
    output: Currency
  }>({
    input: data?.inputCurrency,
    output: data?.outputCurrency
  })
  const [priceActionWithEditMode, setPriceActionWithEditMode] = useState<
    PriceActionWithEditMode[]
  >([])
  const [editName, setEditName] = useState<boolean>(false)

  const [generatePriceClass, { loading: loadingForGenerate }] = useMutation(
    PRICE_CLASSES_GENERATE_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (resp) => {
        toast.success(resp.priceClassGenerate.message)
        onClose()
        refetch()
      },
      onError: (e) => {
        toast.error(e.message)
      }
    }
  )

  const [modifyPriceClass, { loading: loadingForModify }] = useMutation(
    PRICE_CLASSES_MODIFY_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (resp) => {
        toast.success(resp.priceClassModify.message)
        onClose()
        refetch()
      },
      onError: (e) => {
        toast.error(e.message)
      }
    }
  )

  useEffect(() => {
    if (data) {
      const translatedList = normalizeNameTranslatedList(
        data.nameTranslatedList,
        getPersianTranslationValue(data.nameTranslatedList)
      )
      setPriceClassNameTranslatedList(translatedList)
      setPriceClassName({
        EN: data.name,
        FA: getPersianTranslationValue(translatedList)
      })
      // First pass: calculate input/output currency
      const tempPriceActionWithEditMode = data.priceActions.map((item) => {
        const temp: PriceActionWithEditMode = {
          editing: false,
          id: generateRandomString(5),
          priceAction: {
            ...item
          }
        }

        return temp
      })

      const errors = recalcPriceActionErrors(
        tempPriceActionWithEditMode,
        currencies.input,
        currencies.output
      )
      const withErrors = tempPriceActionWithEditMode.map((item, index) => ({
        ...item,
        error: errors[index]
      }))

      setPriceActionWithEditMode(withErrors)
    }
  }, [data])

  useEffect(() => {
    setCurrencies({
      input: data?.inputCurrency,
      output: data?.outputCurrency
    })
  }, [data])

  const onDoneButtonClick = () => {
    const persianName =
      priceClassNameTranslatedList.find(
        (item) =>
          item.label === persianTranslation.label ||
          item.nativeLabel === persianTranslation.nativeLabel
      )?.value || priceClassName.FA
    /*
     * A Persian name is REQUIRED, and an empty one is not merely cosmetic — it stops the
     * price class from discounting anything at all.
     *
     * The chain, traced on staging 2026-09-05:
     *
     *   1. the schema says `TypeNameInput { FA: String!, EN: String! }`, which forbids null
     *      but NOT "" — so an empty Persian name is a valid mutation
     *   2. asset stores it as `FA string \`bson:"FA,omitempty"\`` while EN carries no
     *      omitempty (asset/internal/infra/models/priceClass.go:41-43). BSON therefore
     *      DROPS the FA key entirely when the value is empty, and keeps EN when it is not
     *   3. the legacy pricing path reads that document and asserts the key exists —
     *      `MultilingualText::fromArray` (adotel .../PriceClass/ValueObjects) does
     *      `Assert::keyExists($data, 'FA')` and throws "Expected the key \"FA\" to exist."
     *   4. that call sits at PriceClassCalculator.php:149, BEFORE the price actions load at
     *      158, so the whole calculation aborts and NEITHER the discount nor the sell rate
     *      is applied. Reported as two separate bugs on 1405/06/14; one cause.
     *
     * 74 occurrences in staging's laravel log on 2026-09-05, zero on the two days before.
     *
     * This guard is the cheap half of the fix and the only half that lives in this repo.
     * The asymmetric `omitempty` in asset is the real defect and is not ours to change here
     * — see BL-089. Blocking the empty submit stops NEW price classes from breaking pricing;
     * it does not repair one already stored without the key.
     */
    if (!persianName.trim()) {
      toast.error(getTranslate('Enter a Persian name for the price class.'))
      return
    }

    const translatedNames = priceClassNameTranslatedList.filter(
      (item) => item.label && item.nativeLabel && item.value
    )
    const name = {
      EN: priceClassName.EN,
      FA: persianName
    }
    /*
     * The SAME defect, one level down, and the name guard above does not cover it.
     *
     * PriceAction.php:49 also calls MultilingualText::fromArray — on the action's
     * `description` — so an action whose Persian description is empty is dropped by the same
     * `bson:"FA,omitempty"` and throws in PriceActionsCollection::fromArray, which is the
     * line 158 the name guard exists to let us reach. Fixing only the name would move the
     * abort from 149 to 158 and change nothing an operator can see.
     *
     * The index access is also unguarded: `descriptionTranslatedList[0]` is a TypeError on an
     * empty array, so it is read only after the length check.
     */
    const actionMissingPersian = priceActionWithEditMode.findIndex(
      (item) =>
        item.priceAction.descriptionTranslatedList.length === 0 ||
        !item.priceAction.descriptionTranslatedList[0].value.trim()
    )
    if (actionMissingPersian !== -1) {
      toast.error(
        getTranslate('Enter a Persian description for price action {order}.', {
          order: actionMissingPersian + 1
        })
      )
      return
    }

    const tempPricAction = priceActionWithEditMode.map((item) => {
      const temp: PriceActionForGenerate = {
        actionAmount: item.priceAction.actionAmount,
        actionType: item.priceAction.actionType,
        beneficiary: item.priceAction.beneficiary,
        order: item.priceAction.order,
        description: {
          EN: item.priceAction.description,
          FA: item.priceAction.descriptionTranslatedList[0].value
        }
      }
      return temp
    })

    if (data?.id) {
      modifyPriceClass({
        variables: {
          input: {
            name,
            nameTranslatedList: translatedNames,
            priceActions: tempPricAction,
            inputCurrency: currencies.input,
            outputCurrency: currencies.output
          },
          priceClassID: data.id
        }
      })
    } else {
      generatePriceClass({
        variables: {
          input: {
            name,
            nameTranslatedList: translatedNames,
            priceActions: tempPricAction,
            inputCurrency: currencies.input,
            outputCurrency: currencies.output
          }
        }
      })
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  function handleDragEnd(event: any) {
    const { active, over } = event

    if (active.id !== over.id) {
      setPriceActionWithEditMode((prevItems) => {
        const oldIndex = prevItems.findIndex((i) => i.id === active.id)
        const newIndex = prevItems.findIndex((i) => i.id === over.id)

        const updatedItems = arrayMove(prevItems, oldIndex, newIndex)

        // final output currency = last NON-DisplayRate action (a trailing sink is not the output)
        const finalOutputCurrency = netOutputCurrency(
          updatedItems,
          currencies.input
        )

        const errors = recalcPriceActionErrors(
          updatedItems,
          currencies.input,
          finalOutputCurrency
        )

        // Set the 'order' property based on the new index and 'error' from currencies
        const updatedItemsWithOrderAndError: PriceActionWithEditMode[] =
          updatedItems.map((item, index) => {
            const current = item.priceAction

            return {
              ...item,
              priceAction: {
                ...current,
                order: index + 1
              },
              error: errors[index]
            }
          })

        setCurrencies({
          ...currencies,
          output: finalOutputCurrency
        })

        return updatedItemsWithOrderAndError
      })
    }
  }

  const isPersian = persianRegex.test(priceClassName.FA)
  const isPersianNameEN = persianRegex.test(priceClassName.EN)

  const disabled =
    !priceClassName.EN ||
    !priceClassName.FA ||
    !currencies.input ||
    priceClassNameTranslatedList.some((item, index) =>
      index > 0 && (!!item.label || !!item.nativeLabel || !!item.value)
        ? !item.label || !item.nativeLabel || !item.value
        : false
    )

  const priceActionRowHasError = priceActionWithEditMode.some(
    (ele) => ele.error
  )

  return (
    <Modal
      open={isOpen}
      onClose={() => onClose()}
      dir={getDirection()}
      className={classes.modal}>
      <Slide direction="down" in={isOpen}>
        <Paper className={classes.deletePaper}>
          <div>
            <div className={classes.header}>
              <Typography variant="h2">
                {getTranslate(
                  data?.id ? 'Edit Price Class' : 'Generate Price Class'
                )}
              </Typography>
            </div>
            <div className={classes.inputContainer}>
              {priceActionWithEditMode.length === 0 || editName ? (
                <div className={classes.selectNamesContainer}>
                  <InputWithLabel
                    label={getTranslate('English Name')}
                    placeholder={getTranslate('Enter price class english name')}
                    autoComplete="off"
                    fullWidth
                    inputSize="small"
                    startIcon={<LocalOfferOutlinedIcon />}
                    value={priceClassName.EN}
                    onChange={(event) =>
                      setPriceClassName({
                        ...priceClassName,
                        EN: event.currentTarget.value
                      })
                    }
                  />
                  <InputWithLabel
                    label={getTranslate('Persian Name')}
                    placeholder={getTranslate('Enter price class persian name')}
                    autoComplete="off"
                    fullWidth
                    inputSize="small"
                    startIcon={<LocalOfferOutlinedIcon />}
                    style={isPersian ? { fontFamily: 'IranSans' } : undefined}
                    value={priceClassName.FA}
                    onChange={(event) => {
                      const value = event.currentTarget.value
                      setPriceClassName({
                        ...priceClassName,
                        FA: value
                      })
                      setPriceClassNameTranslatedList((prev) => {
                        const normalized = normalizeNameTranslatedList(prev)
                        return normalized.map((item, index) =>
                          index === 0 ? { ...item, value } : item
                        )
                      })
                    }}
                  />
                  {priceClassNameTranslatedList.slice(1).map((item, index) => {
                    const translatedIndex = index + 1
                    return (
                      <div
                        key={`${translatedIndex}-${item.label}`}
                        className={classes.translationRow}>
                        <InputWithLabel
                          label={getTranslate('Language Code')}
                          placeholder={getTranslate('e.g. AR')}
                          autoComplete="off"
                          fullWidth
                          inputSize="small"
                          value={item.label}
                          onChange={(event) => {
                            const value = event.currentTarget.value
                            setPriceClassNameTranslatedList((prev) =>
                              prev.map((translated, currentIndex) =>
                                currentIndex === translatedIndex
                                  ? { ...translated, label: value }
                                  : translated
                              )
                            )
                          }}
                        />
                        <InputWithLabel
                          label={getTranslate('Native Label')}
                          placeholder={getTranslate('e.g. العربية')}
                          autoComplete="off"
                          fullWidth
                          inputSize="small"
                          value={item.nativeLabel}
                          onChange={(event) => {
                            const value = event.currentTarget.value
                            setPriceClassNameTranslatedList((prev) =>
                              prev.map((translated, currentIndex) =>
                                currentIndex === translatedIndex
                                  ? { ...translated, nativeLabel: value }
                                  : translated
                              )
                            )
                          }}
                        />
                        <InputWithLabel
                          label={getTranslate('Translated Name')}
                          placeholder={getTranslate('Enter translated name')}
                          autoComplete="off"
                          fullWidth
                          inputSize="small"
                          value={item.value}
                          onChange={(event) => {
                            const value = event.currentTarget.value
                            setPriceClassNameTranslatedList((prev) =>
                              prev.map((translated, currentIndex) =>
                                currentIndex === translatedIndex
                                  ? { ...translated, value }
                                  : translated
                              )
                            )
                          }}
                        />
                        <IconButton
                          className={classes.removeTranslationButton}
                          aria-label={getTranslate('Remove language')}
                          onClick={() => {
                            setPriceClassNameTranslatedList((prev) =>
                              prev.filter(
                                (_, currentIndex) =>
                                  currentIndex !== translatedIndex
                              )
                            )
                          }}>
                          <DeleteOutlineOutlinedIcon />
                        </IconButton>
                      </div>
                    )
                  })}
                  <Button
                    color="primary"
                    className={classes.addLanguageButton}
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => {
                      setPriceClassNameTranslatedList((prev) => [
                        ...prev,
                        {
                          label: '',
                          nativeLabel: '',
                          value: ''
                        }
                      ])
                    }}>
                    {getTranslate('Add language')}
                  </Button>
                  <div className={classes.currencyContainer}>
                    <Typography>{getTranslate('Input Currency')}:</Typography>
                    <Select
                      value={currencies.input || ''}
                      onChange={(event) => {
                        setCurrencies({
                          ...currencies,
                          input: event.target.value as Currency
                        })
                      }}>
                      {getCurrenciesAvailable().map((currency) => (
                        <MenuItem
                          key={currency}
                          value={currency}
                          className={classes.menuItemRight}>
                          {currency}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              ) : (
                <div className={classes.priceClassNameWithCurrency}>
                  <div className={classes.priceClassNameContainer}>
                    <Typography
                      variant="caption"
                      className={[
                        classes.titleContainer,
                        classes.titleContainerFontSize
                      ].join(' ')}>
                      {getTranslate('Name')}
                    </Typography>
                    <div className={classes.nameAndEditIcon}>
                      <Typography
                        variant="h2"
                        style={
                          isPersianNameEN
                            ? { fontFamily: 'IranSans' }
                            : undefined
                        }
                        className={classes.titleContainer}>
                        {lang === 'en' ? priceClassName.EN : priceClassName.FA}
                      </Typography>
                      <IconButton
                        className={classes.editIconContainer}
                        onClick={() => {
                          setEditName(true)
                        }}>
                        <EditIcon />
                      </IconButton>
                    </div>
                  </div>
                  <div className={classes.currencyContainer}>
                    <Typography>{getTranslate('Input Currency')}:</Typography>
                    <Select
                      value={currencies.input || ''}
                      disabled
                      onChange={(event) => {
                        setCurrencies({
                          ...currencies,
                          input: event.target.value as Currency
                        })
                      }}>
                      {getCurrenciesAvailable().map((currency) => (
                        <MenuItem
                          key={currency}
                          value={currency}
                          className={classes.menuItemRight}>
                          {currency}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={classes.flowBlock}>
            <div className={classes.flowRail}>
              <span className={classes.flowRailLine} />
              <span className={classes.flowRailDot} />
            </div>
            <div className={classes.flowStack}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}>
                <SortableContext
                  items={priceActionWithEditMode.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}>
                  {priceActionWithEditMode.map((item, index) =>
                    item.editing ? (
                      <AddPriceActionForm
                        key={item.id}
                        setPriceActionWithEditMode={setPriceActionWithEditMode}
                        index={index}
                        priceActionWithEditMode={priceActionWithEditMode}
                        currencies={currencies}
                        setCurrencies={setCurrencies}
                      />
                    ) : (
                      <PriceActionRow
                        key={item.id}
                        setPriceActionWithEditMode={setPriceActionWithEditMode}
                        index={index}
                        priceActionWithEditMode={priceActionWithEditMode}
                        priceAction={item.priceAction}
                        id={item.id}
                        archivedAt={data?.archivedAt}
                        currencies={currencies}
                      />
                    )
                  )}
                </SortableContext>
              </DndContext>
            </div>
          </div>
          {!priceActionWithEditMode.some((i) => i.editing) && (
            <div className={classes.addPriceActionWithOutpotCurrency}>
              <div
                className={[
                  classes.addSectionTitle,
                  disabled && classes.addSectionTitleDisabled
                ].join(' ')}
                onClick={() => {
                  if (!disabled) {
                    setPriceActionWithEditMode([
                      ...priceActionWithEditMode,
                      {
                        editing: true,
                        error: false,
                        id: generateRandomString(5),
                        priceAction: {
                          actionType: '',
                          actionAmount: {
                            amount: undefined,
                            type: undefined,
                            fromCurrency: '',
                            toCurrency: '',
                            direction: undefined
                          },
                          beneficiary: undefined,
                          order: priceActionWithEditMode.length + 1,
                          description: '',
                          descriptionTranslatedList: [
                            {
                              label: 'Iran, Islamic Republic of',
                              nativeLabel: 'فارسی',
                              value: ''
                            }
                          ]
                        }
                      }
                    ])
                  }
                }}>
                <IconButton
                  className={classes.addButtonContainer}
                  disabled={disabled}>
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
                <Typography
                  variant="h4"
                  className={[
                    classes.addButtonText,
                    disabled && classes.addButtonTextDisabled
                  ].join(' ')}>
                  {getTranslate('Add Price Action')}
                </Typography>
              </div>
              {priceActionWithEditMode.length !== 0 && (
                <div
                  className={[
                    classes.currencyContainer,
                    classes.outputCurrencyContainer
                  ].join(' ')}>
                  <Typography>{getTranslate('Output Currency')}:</Typography>
                  <Select
                    value={currencies.output ? currencies.output : ''}
                    disabled>
                    {getCurrenciesAvailable().map((currency) => (
                      <MenuItem
                        key={currency}
                        value={currency}
                        className={classes.menuItemRight}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          )}
          {priceActionWithEditMode.length > 0 &&
            priceActionWithEditMode.every((pr) => !pr.editing) && (
              <div className={classes.buttonsContainer}>
                <Button
                  className={classes.cancelButton}
                  onClick={() => {
                    setPriceActionWithEditMode([])
                    onClose()
                  }}>
                  {getTranslate('Cancel')}
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={onDoneButtonClick}
                  disabled={
                    loadingForGenerate ||
                    loadingForModify ||
                    !!data?.archivedAt ||
                    priceActionRowHasError
                  }>
                  {getTranslate('Done')}
                  {(loadingForGenerate || loadingForModify) && (
                    <CircularProgress
                      className={classes.circularProgress}
                      size={16}
                      color="primary"
                    />
                  )}
                </Button>
              </div>
            )}
        </Paper>
      </Slide>
    </Modal>
  )
}

export default GeneratePriceClassModal
